import {
  IDraftMessage,
  IDraftRecommendation,
  IDraftRunVersion,
} from "../../../store/queries/contractModelDrafts";
import { openQuestionsOf } from "../utils";
import { WIZARD_STEPS } from "./steps";

/**
 * CLE-2811 — the run as ONE conversation. Pure helpers, so the ordering rules are tested without a DOM.
 */

export type IThreadItem =
  | { kind: "version"; key: string; versionNo: number; createdAt: string }
  /**
   * CLE-2811 — the run instructions she gave before the first generation, shown as her first message.
   * Synthetic (no message row behind it), so it is never queued and never deletable.
   */
  | { kind: "instructions"; key: string; body: string }
  | {
      kind: "message";
      key: string;
      message: IDraftMessage;
      /** Her message, not yet consumed by any pass. Only these can be deleted. */
      queued: boolean;
      /** Her optimistic message, before the server has echoed it back. */
      sending?: boolean;
    };

export const isAssistantMessage = (m: IDraftMessage): boolean =>
  m.role === "assistant";

export const isQueuedMessage = (m: IDraftMessage): boolean =>
  !isAssistantMessage(m) && !m.appliedInVersionId;

const time = (iso: string): number => new Date(iso).getTime();

/** The stable key of the run-instructions bubble: one per run, never a message id. */
export const instructionsKey = (runId: string): string =>
  `instructions:${runId}`;

/**
 * Messages and version boundaries in one chronological thread. A version separator sits at the moment
 * that pass was started, so the thread reads as "what she asked → Version N → what the agent answered".
 * Version 1 is left out: it is created with the run, before anything is said, and a separator at the
 * very top of the thread says nothing. On a timestamp tie the separator goes first — a pass's own
 * reply can never precede the pass.
 *
 * CLE-2811 — the run's instructions (`generationInstructions`), when there are any, open the thread as
 * her first bubble: they are what she said before the first generation, so they precede every message
 * and every separator. They used to sit in a card above the wizard, apart from the conversation.
 */
export const buildThread = (
  messages: IDraftMessage[],
  versions: IDraftRunVersion[],
  instructions?: { runId: string; body: string | null }
): IThreadItem[] => {
  const items: { at: number; order: number; item: IThreadItem }[] = [];
  versions
    .filter((v) => v.versionNo > 1)
    .forEach((v) =>
      items.push({
        at: time(v.createdAt),
        order: 0,
        item: {
          kind: "version",
          key: `version-${v.id}`,
          versionNo: v.versionNo,
          createdAt: v.createdAt,
        },
      })
    );
  messages.forEach((m) =>
    items.push({
      at: time(m.createdAt),
      order: 1,
      item: {
        kind: "message",
        key: m.id,
        message: m,
        queued: isQueuedMessage(m),
      },
    })
  );
  // Array.prototype.sort is stable, so equal (at, order) keep their server order.
  const thread = items
    .sort((a, b) => a.at - b.at || a.order - b.order)
    .map((x) => x.item);
  const body = instructions?.body?.trim();
  if (!instructions || !body) return thread;
  return [
    { kind: "instructions", key: instructionsKey(instructions.runId), body },
    ...thread,
  ];
};

/**
 * Every distinct open question on the current version, in recommendation order. A withdrawn
 * (`superseded`) or rejected recommendation's questions are moot — nobody is building it.
 */
export const collectOpenQuestions = (
  recs: IDraftRecommendation[]
): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  recs
    .filter((rec) => rec.status !== "superseded" && rec.status !== "rejected")
    .forEach((rec) =>
      openQuestionsOf(rec).forEach((q) => {
        if (!seen.has(q)) {
          seen.add(q);
          out.push(q);
        }
      })
    );
  return out;
};

/** A bubble longer than this collapses behind "Show all". */
export const COLLAPSE_MAX_LINES = 40;
/**
 * The same bound in characters, for text with few line breaks — one 20,000-character paragraph is
 * hundreds of wrapped lines in a 380px panel. ~40 lines of ~100 characters.
 */
export const COLLAPSE_MAX_CHARS = 4000;

export interface ICollapsedText {
  /** What the bubble shows while collapsed — the whole text when nothing is hidden. */
  shown: string;
  /** True when `shown` is a prefix and a "Show all" toggle is needed. */
  collapsed: boolean;
  /** Lines of the full text not in `shown` (a partial line counts as hidden). 0 when not collapsed. */
  hiddenLines: number;
}

/**
 * CLE-2811 — the agent now answers the way a chat assistant does, so a reply can be a full rate list
 * (every grouper / CPT row, per plan). The first COLLAPSE_MAX_LINES lines (cut at COLLAPSE_MAX_CHARS)
 * are shown; the rest waits behind "Show all". Pure, so the cut is tested without a DOM.
 */
export const collapseLongText = (
  text: string,
  maxLines: number = COLLAPSE_MAX_LINES,
  maxChars: number = COLLAPSE_MAX_CHARS
): ICollapsedText => {
  const lines = text.split("\n");
  if (lines.length <= maxLines && text.length <= maxChars) {
    return { shown: text, collapsed: false, hiddenLines: 0 };
  }
  let shown = lines.slice(0, maxLines).join("\n");
  if (shown.length > maxChars) {
    const cut = shown.slice(0, maxChars);
    // Prefer to end on a whole line; a single giant line is cut where it must be.
    const lastBreak = cut.lastIndexOf("\n");
    shown = lastBreak > 0 ? cut.slice(0, lastBreak) : cut;
  }
  const shownLines = shown.split("\n").length;
  const partial = !text.startsWith(`${shown}\n`) && shown !== text ? 1 : 0;
  return {
    shown,
    collapsed: true,
    hiddenLines: Math.max(lines.length - shownLines + partial, 1),
  };
};

/** "Contract Terms" for `terms`; null for no step or one the wizard no longer has. */
export const stepTitle = (stepId: string | null): string | null =>
  stepId ? WIZARD_STEPS.find((s) => s.id === stepId)?.title ?? null : null;

/** "15:20" — the thread is read on the day it is written; the date would be noise. */
export const formatThreadTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
